import React from 'react';
import PropTypes from 'prop-types';
import { Col, FormGroup, Input, Label, Alert } from 'reactstrap';
import Select from 'react-select';
import Switch from 'react-switch';
import VendorMultiSelect from '../section/VendorMultiSelect';

const selectTheme = theme => ({
  ...theme,
  borderRadius: 5,
  colors: {
    ...theme.colors,
    primary25: '#253746',
    primary: '#253746',
    neutral10: '#253746',
    dangerLight: '#bf6e4c'
  }
});

/**
 * Renders a post-credential widget from a schema widget definition.
 * Supports types: select, vendorMultiSelect, checkbox, toggle.
 *
 * Widget schema shape:
 * {
 *   type: "select" | "vendorMultiSelect" | "checkbox" | "toggle",
 *   id: string,
 *   label: string,
 *   stateKey: string,        // Key in dynamicState
 *   required: boolean,
 *   options: Array,           // For static options
 *   optionsSource: string,    // "static" or key in dynamicState/props for dynamic options
 *   condition: string,        // Optional: stateKey that must be truthy to show widget
 *   feedback: string,         // Validation feedback text
 *   colSize: number,          // Bootstrap col size
 *   className: string,        // Optional CSS class
 *   conditionalAlerts: Array  // Optional alerts based on value
 * }
 */
const DynamicPostCredWidget = ({ widget, value, options, onChange, isSubmitted, isVisible }) => {
  if (!isVisible) return null;

  const colSize = widget.colSize || 6;

  switch (widget.type) {
    case 'select':
      return (
        <Col lg={String(colSize)} className={widget.className || ''}>
          <FormGroup>
            <Label>
              {widget.label}
              {widget.required ? <span style={{ color: '#bd472a' }}>*</span> : null}
            </Label>
            <div data-testid={`dynamic-widget-${widget.id || widget.stateKey}`}>
              <Select
                value={value}
                options={options || widget.options || []}
                onChange={selected => onChange(widget.stateKey, selected)}
                theme={selectTheme}
                classNamePrefix="select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                isOptionDisabled={option => option.isDisabled}
                formatOptionLabel={option => (
                  <div>
                    <div>{option.label}</div>
                    {option.description && <small className="text-muted">{option.description}</small>}
                  </div>
                )}
              />
            </div>
            {widget.conditionalAlerts &&
              widget.conditionalAlerts.map((alert, i) => {
                // Simple condition evaluation: "value === 'someVal'"
                const conditionMet = value?.value && evaluateCondition(alert.condition, value);
                return conditionMet ? (
                  <Alert key={i} color={alert.color || 'info'} className="mt-2">
                    {alert.message}
                  </Alert>
                ) : null;
              })}
          </FormGroup>
        </Col>
      );

    case 'vendorMultiSelect': {
      const hasOptions = Array.isArray(options) && options.length > 0;
      if (!hasOptions && widget.emptyAlert) {
        return (
          <Col lg={String(colSize)} className={widget.className || ''}>
            <FormGroup>
              <Label>
                {widget.label}
                {widget.required ? <span style={{ color: '#bd472a' }}>*</span> : null}
              </Label>
              <Alert color={widget.emptyAlert.color || 'warning'} fade={false}>
                {widget.emptyAlert.title ? <strong>{widget.emptyAlert.title}: </strong> : null}
                {widget.emptyAlert.message}
              </Alert>
            </FormGroup>
          </Col>
        );
      }
      return (
        <Col lg={String(colSize)} className={widget.className || ''}>
          <VendorMultiSelect
            options={options || []}
            setHook={selected => onChange(widget.stateKey, selected)}
            categories={Array.isArray(value) ? value : []}
            handleSelectChange={(e, setHook) => setHook(e || [])}
            isSubmitted={isSubmitted}
            feedback={widget.feedback || 'an item'}
            name={widget.name || widget.label}
            required={widget.required}
          />
        </Col>
      );
    }

    case 'checkbox':
      return (
        <Col lg={String(colSize)} className={widget.className || ''}>
          <FormGroup check className="mt-2">
            <Input
              type="checkbox"
              id={widget.id || widget.stateKey}
              checked={!!value}
              onChange={e => onChange(widget.stateKey, e.target.checked)}
              data-testid={`dynamic-widget-${widget.id || widget.stateKey}`}
            />
            <Label check htmlFor={widget.id || widget.stateKey}>
              {widget.label}
            </Label>
          </FormGroup>
        </Col>
      );

    case 'toggle':
      return (
        <Col lg={String(colSize)} className={widget.className || ''}>
          <FormGroup className="mt-2">
            <Label className="d-flex align-items-center">
              <Switch
                checked={!!value}
                onChange={checked => onChange(widget.stateKey, checked)}
                onColor="#253746"
                offColor="#888"
                height={20}
                width={40}
                handleDiameter={18}
                data-testid={`dynamic-widget-${widget.id || widget.stateKey}`}
              />
              <span className="ml-2">{widget.label}</span>
            </Label>
          </FormGroup>
        </Col>
      );

    default:
      console.warn(`Unknown widget type: ${widget.type}`);
      return null;
  }
};

/**
 * Simple condition evaluator for conditional alerts.
 * Supports patterns like "value === 'someVal'".
 */
function evaluateCondition(condition, value) {
  if (!condition) return false;
  const match = condition.match(/^value\s*===\s*'(.+)'$/);
  if (match) {
    return value?.value === match[1];
  }
  return false;
}

DynamicPostCredWidget.propTypes = {
  widget: PropTypes.shape({
    type: PropTypes.oneOf(['select', 'vendorMultiSelect', 'checkbox', 'toggle']).isRequired,
    id: PropTypes.string,
    label: PropTypes.string,
    stateKey: PropTypes.string.isRequired,
    required: PropTypes.bool,
    options: PropTypes.array,
    optionsSource: PropTypes.string,
    condition: PropTypes.string,
    feedback: PropTypes.string,
    name: PropTypes.string,
    colSize: PropTypes.number,
    className: PropTypes.string,
    conditionalAlerts: PropTypes.array,
    emptyAlert: PropTypes.shape({
      color: PropTypes.string,
      title: PropTypes.string,
      message: PropTypes.string
    })
  }).isRequired,
  value: PropTypes.any,
  options: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  isSubmitted: PropTypes.bool,
  isVisible: PropTypes.bool
};

export default DynamicPostCredWidget;
